import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { TextField, Button, Typography, Container, Box } from "@mui/material";
import { toast } from "react-toastify";
import { trackEvent, setAmplitudeUserId } from "../analytics";
import Footer from "../LandingPage/Footer";
import TopBar from "../LandingPage/TopBar";

const Login = ({ onAuthSuccess }) => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const navigate = useNavigate();
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [lastTriedEmail, setLastTriedEmail] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowResend(false);
    setLastTriedEmail(formData.email);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/login`,
        formData
      );
      toast.success("Logged in successfully!");
      onAuthSuccess(response.data.token);
      setAmplitudeUserId(formData.email);
      trackEvent("Login", { method: "email", email: formData.email });
      navigate("/app");
    } catch (error) {
      const msg =
        error.response?.data?.message || "Error logging in. Please try again.";
      toast.error(msg);
      if (msg === "Please verify your email before logging in.") {
        setShowResend(true);
      }
      console.error("Error logging in:", error.response?.data || error.message);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/resend-verification`,
        { email: lastTriedEmail }
      );
      toast.success("Verification email resent. Please check your inbox.");
      setShowResend(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to resend verification email."
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Footer
      supportEmail={
        process.env.REACT_APP_SUPPORT_EMAIL || "noreply.reeltale@gmail.com"
      }
      setFeedbackOpen={() => {}} // No feedback modal in Login
    >
      <TopBar onAuthSuccess={onAuthSuccess} />
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-5 py-12 sm:py-16 bg-gradient-to-br from-gray-50 via-white to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="w-full max-w-md">
          <div className="card-modern p-8 md:p-10">
            <div className="text-center mb-8">
              <div className="inline-block mb-4 px-4 py-2 bg-primary-100 dark:bg-primary-900/30 rounded-full">
                <span className="text-primary-700 dark:text-primary-300 text-sm font-semibold">
                  🔐 Welcome Back
                </span>
              </div>
              <Typography variant="h4" className="text-3xl sm:text-4xl font-extrabold mb-3 text-gray-900 dark:text-white">
                Welcome Back
              </Typography>
              <Typography variant="body1" className="text-base sm:text-lg text-gray-600 dark:text-gray-400">
                Log in to continue using ReelTale
              </Typography>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                required
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
              <TextField
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                fullWidth
                required
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-8 py-3.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case text-base sm:text-lg mt-2"
              >
                Log in
              </Button>
            </form>
            <div className="mt-6 space-y-3">
              {showResend && (
                <Button
                  variant="outlined"
                  fullWidth
                  className="border-primary-600 text-primary-600 hover:bg-primary-50 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20 normal-case"
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                >
                  {resendLoading ? "Resending..." : "Resend Verification Email"}
                </Button>
              )}
              <Button
                variant="text"
                fullWidth
                className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 normal-case"
                onClick={() => navigate("/forgot-password")}
              >
                Forgot Password?
              </Button>
            </div>
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
              <span className="text-gray-600 dark:text-gray-400 text-sm">
                Don't have an account?{" "}
                <a
                  href="/signup"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold transition-colors"
                >
                  Sign up
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>
    </Footer>
  );
};

export default Login;
